import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  afterNextRender,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatFormField, MatHint, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { ConfirmDialogComponent } from '../../../components/confirm-dialog/confirm-dialog.component';
import { ObjectMappingService } from '../../../services/object-mapping.service';
import {
  LOCATOR_STRATEGIES,
  LocatorInput,
  OBJECT_TYPES,
  ObjectMappingEntryView,
  ObjectMappingLocator,
} from '../../../models/object-mapping.model';
import { FIELD_LENGTHS } from '../../../shared/field-lengths';
import { isValidObjectName } from '../object-names';
import { isNameTaken, serverMessage } from '../server-message';

/** Wie `ObjectMappingDtos.TEXT_MAX` im BE: Adresse und Kandidatenwert. */
const TEXT_MAX = 2000;
/** Wie `LocatorRules.MAX_CANDIDATES` im BE. */
const MAX_CANDIDATES = 20;

interface LocatorRow {
  key: number;
  strategy: string;
  value: string;
  name: string;
  /** Erst nach einer Eingabe zeigt ein leerer Wert den Hinweis; gesperrt ist Speichern trotzdem. */
  touched: boolean;
}

/**
 * Eintrag einer Objektbibliothek anlegen oder bearbeiten, mit Kandidatenliste (NEX-1705).
 * Ohne `entry` wird angelegt; beim Bearbeiten bleibt der Name fest (Umbenennen hat eigenen Dialog).
 *
 * Design-Luecke: Das Bundle hat keinen Entwurf dafuer. Aufbau wie der Umbenennen-Dialog,
 * naechste Stelle im Bundle ist das Modal in `page-flows.jsx`.
 */
@Component({
  selector: 'app-object-entry-dialog',
  imports: [TranslatePipe, MatFormField, MatLabel, MatHint, MatInput, MatIcon, MatButton, ConfirmDialogComponent],
  templateUrl: './object-entry-dialog.component.html',
  styleUrl: './object-entry-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ObjectEntryDialogComponent implements OnInit {
  private readonly service = inject(ObjectMappingService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  readonly projectId = input.required<string>();
  readonly mappingId = input.required<string>();
  readonly entry = input<ObjectMappingEntryView | null>(null);

  readonly saved = output<ObjectMappingEntryView>();
  readonly cancelled = output<void>();

  readonly nameMax = FIELD_LENGTHS.NAME_MAX;
  readonly textMax = TEXT_MAX;
  readonly maxCandidates = MAX_CANDIDATES;
  readonly strategies: readonly string[] = LOCATOR_STRATEGIES;

  readonly name = signal('');
  readonly typeName = signal('');
  readonly url = signal('');
  readonly rows = signal<LocatorRow[]>([]);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);

  readonly creating = computed(() => !this.entry());
  readonly typeOptions = computed(() => {
    const current = this.entry()?.typeName;
    const types: string[] = [...OBJECT_TYPES];
    return current && !types.includes(current) ? [...types, current] : types;
  });
  readonly showInvalidName = computed(() => this.name().length > 0 && !isValidObjectName(this.name()));
  readonly showValueMissing = computed(() => this.rows().some((row) => row.touched && !row.value.trim()));
  readonly tooLong = computed(
    () =>
      this.url().length > TEXT_MAX ||
      this.rows().some((row) => row.value.length > TEXT_MAX || (isRole(row) && row.name.length > this.nameMax)),
  );
  readonly noCandidates = computed(() => this.rows().length === 0);
  readonly fromFulltag = computed(() => this.noCandidates() && !!this.entry()?.fulltag);
  readonly canSave = computed(
    () =>
      !this.saving() &&
      (!this.creating() || (isValidObjectName(this.name()) && !!this.typeName())) &&
      this.rows().every((row) => !!row.value.trim()) &&
      !this.tooLong() &&
      (!this.noCandidates() || this.fromFulltag()),
  );

  private readonly nameInput = viewChild<ElementRef<HTMLInputElement>>('nameInput');
  private nextKey = 0;

  constructor() {
    afterNextRender(() => this.nameInput()?.nativeElement.focus());
  }

  ngOnInit(): void {
    const entry = this.entry();
    this.typeName.set(entry?.typeName ?? '');
    this.url.set(entry?.url ?? '');
    this.rows.set(entry ? entry.locators.map((locator) => this.toRow(locator)) : [this.toRow({ strategy: 'css' })]);
  }

  strategyOptions(current: string): readonly string[] {
    return this.strategies.includes(current) ? this.strategies : [...this.strategies, current];
  }

  isKnownStrategy(strategy: string): boolean {
    return this.strategies.includes(strategy);
  }

  isRole(row: LocatorRow): boolean {
    return isRole(row);
  }

  onName(event: Event): void {
    this.name.set(inputValue(event));
    this.error.set(null);
  }

  onType(event: Event): void {
    this.typeName.set(inputValue(event));
    this.error.set(null);
  }

  onUrl(event: Event): void {
    this.url.set(inputValue(event));
    this.error.set(null);
  }

  onStrategy(index: number, event: Event): void {
    this.updateRow(index, { strategy: inputValue(event) });
  }

  onValue(index: number, event: Event): void {
    this.updateRow(index, { value: inputValue(event), touched: true });
  }

  onLocatorName(index: number, event: Event): void {
    this.updateRow(index, { name: inputValue(event) });
  }

  addRow(): void {
    if (this.rows().length >= MAX_CANDIDATES) return;
    this.rows.update((rows) => [...rows, this.toRow({ strategy: 'css' })]);
    this.error.set(null);
  }

  removeRow(index: number): void {
    this.rows.update((rows) => rows.filter((_, i) => i !== index));
    this.error.set(null);
  }

  /** Tauscht die Zeile mit ihrer Nachbarin; `step` ist -1 (hoch) oder 1 (runter). */
  move(index: number, step: -1 | 1): void {
    const target = index + step;
    const rows = [...this.rows()];
    if (target < 0 || target >= rows.length) return;
    [rows[index], rows[target]] = [rows[target], rows[index]];
    this.rows.set(rows);
    this.error.set(null);
  }

  save(): void {
    if (!this.canSave()) return;
    this.saving.set(true);
    this.error.set(null);
    const fields = {
      ...(this.typeName() ? { typeName: this.typeName() } : {}),
      ...(this.url().trim() ? { url: this.url().trim() } : {}),
      locators: this.rows().map(toLocator),
    };
    const entry = this.entry();
    const request = entry
      ? this.service.updateEntry(this.projectId(), this.mappingId(), entry.id, fields)
      : this.service.createEntry(this.projectId(), this.mappingId(), { logicalName: this.name(), ...fields });
    request.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (view) => {
        this.saving.set(false);
        this.saved.emit(view);
      },
      error: (err: unknown) => {
        this.error.set(this.messageFor(err));
        this.saving.set(false);
      },
    });
  }

  private updateRow(index: number, change: Partial<LocatorRow>): void {
    this.rows.update((rows) => rows.map((row, i) => (i === index ? { ...row, ...change } : row)));
    this.error.set(null);
  }

  /** Das BE kennt nur `strategy`, `value` und `name`; weitere Classic-Schluessel fallen beim Speichern weg. */
  private toRow(locator: ObjectMappingLocator): LocatorRow {
    return {
      key: this.nextKey++,
      strategy: locator['strategy'] ?? '',
      value: locator['value'] ?? '',
      name: locator['name'] ?? '',
      touched: false,
    };
  }

  private messageFor(err: unknown): string {
    if (isNameTaken(err)) return this.translate.instant('objectMappings.entryDialog.nameTaken');
    return serverMessage(err) ?? this.translate.instant('objectMappings.entryDialog.error');
  }
}

function isRole(row: LocatorRow): boolean {
  return row.strategy === 'role';
}

function toLocator(row: LocatorRow): LocatorInput {
  return isRole(row) && row.name.trim()
    ? { strategy: row.strategy, value: row.value, name: row.name }
    : { strategy: row.strategy, value: row.value };
}

function inputValue(event: Event): string {
  return (event.target as HTMLInputElement | HTMLSelectElement).value;
}
